import {
  buildOrderLiteral,
  buildPagingLiteral,
  buildWhereLiteral,
  OrderBy,
} from '@/lib/graphql-query-builder';

/**
 * The Data Gateway's `get<Schema>s` queries take native `where` / `order` / `paging` arguments
 * instead of a single JSON-string `input: DynamicQueryInput` — see graphql-query-builder.ts for
 * why these are inlined as literals rather than passed as typed variables. Paged lists sort on the
 * server (`buildOrderLiteral`), so each page is a slice of the sorted whole.
 *
 * Schema: MbmProject. The gateway names query operations `get<Collection>`; the alias keeps the
 * response key `MbmProjects`.
 *
 * Latest change first by default, like the old project list's `{ LastUpdateDate: -1 }` sort.
 */
export const DEFAULT_MBM_PROJECTS_ORDER: OrderBy[] = [
  { field: 'LastUpdatedDate', direction: 'DESC' },
];

export const buildGetMbmProjectsQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number,
  order: OrderBy[] = DEFAULT_MBM_PROJECTS_ORDER
) => `
  query {
    MbmProjects: getMbmProjects(
      where: ${buildWhereLiteral(where)}
      order: ${buildOrderLiteral(order)}
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      hasNextPage
      hasPreviousPage
      totalCount
      totalPages
      pageSize
      pageNo
      items {
        ItemId
        CreatedDate
        LastUpdatedDate
        LastUpdatedBy
        ProjectId
        ProjectName
        CustomerId
        CustomerName
        Status
        MbmProjectHistory
      }
    }
  }
`;

// Customers are not a schema of their own: they are the distinct CustomerId/CustomerName pairs of
// the projects, so this only asks for those two columns. Latest change first, like the old customer
// list's `{ LastUpdateDate: -1 }` sort, so a customer keeps the name of its latest-changed project.
export const buildGetMbmCustomersQuery = (
  where: Record<string, unknown>,
  pageNo: number,
  pageSize: number
) => `
  query {
    MbmProjects: getMbmProjects(
      where: ${buildWhereLiteral(where)}
      order: ${buildOrderLiteral([{ field: 'LastUpdatedDate', direction: 'DESC' }])}
      paging: ${buildPagingLiteral(pageNo, pageSize)}
    ) {
      totalCount
      items {
        CustomerId
        CustomerName
      }
    }
  }
`;
